<script setup>
  import { storeToRefs } from "pinia";
  import { watch, ref, nextTick, computed } from "vue";
  import { Plus } from "lucide-vue-next";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
  import { Badge } from "@/components/ui/badge";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Button } from "@/components/ui/button";
  import { Label } from "@/components/ui/label";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useWeightingsStore } from "@stores/weightings";
  import WeightingQuota from "@/components/Survey/Weighting/WeightingQuota.vue";

  const weightingsStore = useWeightingsStore();
  const { selectedWeightingIndex, selectedWeighting, hasDuplicatedTargets, defaultQuotasEnabled, saveAttempted } =
    storeToRefs(weightingsStore);
  const { addQuotas, generateDefaultQuotas } = weightingsStore;

  const surveyStore = useSurveyStore();
  const { survey } = storeToRefs(surveyStore);

  const props = defineProps({ isFirstLoaded: Boolean });
  const changedIndex = ref(null);

  const targetOptions = computed(() =>
    survey.value.targets.map((t) => ({ value: String(t.id), label: t.name })),
  );

  const totalFromTargets = computed(() => {
    if (selectedWeighting.value === null) return 0;
    const selectedTargets = selectedWeighting.value.targets.map((id) =>
      survey.value.targets.find((target) => target.id === id),
    );
    return selectedTargets.reduce((acc, target) => acc + target.target, 0);
  });

  const handleTargetSelect = (val) => {
    selectedWeighting.value.targets = val.map(Number);
    handleTargetsChange();
  };

  const handleTargetsChange = () => {
    if (selectedWeighting.value) selectedWeighting.value.total = totalFromTargets.value;
  };

  watch(
    [
      () => selectedWeighting.value?.name,
      () => selectedWeighting.value?.total,
      () => JSON.stringify(selectedWeighting.value?.targets),
      () =>
        JSON.stringify(
          selectedWeighting.value?.quotas.map((x) => [
            x.question_name,
            x.question_id,
            x.target,
            x.answers.map((a) => a.target),
          ]),
        ),
    ],
    () => {
      if (changedIndex.value || !props.isFirstLoaded) return;
      if (selectedWeighting.value) selectedWeighting.value.saved = false;
    },
  );

  watch(selectedWeightingIndex, () => {
    // handle watcher change but not select change
    changedIndex.value = true;
    nextTick(() => {
      changedIndex.value = false;
    });
  });
</script>

<template>
  <Card v-if="selectedWeighting !== null">
    <CardHeader class="p-4">
      <CardTitle>Edit {{ selectedWeighting.name }}</CardTitle>
    </CardHeader>
    <CardContent class="space-y-4 px-4 pb-4 pt-0">
      <Badge
        v-if="saveAttempted && hasDuplicatedTargets"
        variant="destructive">
        This weighting shares one or more targets with an other weighting.
      </Badge>
      <div class="space-y-2">
        <Label>Name</Label>
        <Input v-model="selectedWeighting.name" />
      </div>
      <div class="space-y-2">
        <Label>Target</Label>
        <Select
          :model-value="selectedWeighting.targets.map(String)"
          :options="targetOptions"
          :multiple="true"
          placeholder="Select a target"
          @update:model-value="handleTargetSelect" />
      </div>
      <div class="space-y-2">
        <Label>Total Target (N)</Label>
        <Input
          v-model="selectedWeighting.total"
          :placeholder="String(totalFromTargets)" />
      </div>
      <WeightingQuota
        v-for="(quota, index) in selectedWeighting.quotas"
        :key="index"
        :index="index" />
      <div class="flex gap-2">
        <Button
          variant="outline"
          class="shadow-xs"
          @click="addQuotas()">
          <Plus class="h-4 w-4" />
          Add quotas
        </Button>
        <Button
          v-if="defaultQuotasEnabled"
          variant="outline"
          class="shadow-xs"
          @click="generateDefaultQuotas()">
          <AiStar class="h-4 w-4" />
          Generate default quotas
        </Button>
      </div>
    </CardContent>
  </Card>
</template>
